import * as React from 'react';

import { VisuallyHidden } from '@reach/visually-hidden';
import axios from 'axios';
import type { AxiosProgressEvent } from 'axios';
import { useForm } from 'react-hook-form';
import type { FieldErrors } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import { Button } from 'stile-shared/src/components/2024/common/Button';
import {
  ComboBox,
  Form,
  FormField,
  FormFieldError,
  Input,
  Select,
  TextArea,
} from 'stile-shared/src/components/2024/common/Form';
import type {
  StileAuGapApplicationPayload,
  StileGapApplicationFields,
  StileGapApplicationFormData,
  StileGapApplicationLetterPayload,
} from 'stile-shared/src/types/gapApplication';
import { tagEvent } from 'stile-shared/src/utils/tagEvent';
import { emailValidator, requiredNotBlank } from 'stile-shared/src/utils/validators';

import { staffOnlyRoleSelectOptions } from 'utils/form';

import { auSchools } from 'data/schools-au';

// Two-phase submission endpoints — contract defined in the backend's
// gap_application.proto (public-api2).
const GAP_APPLICATION_FIELDS_API = 'https://stileapp.com/api/au/v3/gapApplications/fields';
const GAP_APPLICATION_LETTER_API = 'https://stileapp.com/api/au/v3/gapApplications/letter';

const MAX_LETTER_BYTES = 10 * 1024 * 1024;

// Phase-2 letter upload: total attempts before falling back to "send it by
// email". Jittered backoff between attempts (per the backend contract), none
// after the last.
const LETTER_MAX_ATTEMPTS = 3;
const LETTER_RETRY_BACKOFF_MS = [2000, 8000];

function jitteredBackoff(baseMs: number) {
  return baseMs + Math.round(Math.random() * baseMs * 0.5);
}

// The extension is the single source of truth for the letter's content type:
// file.type is unreliable (empty for .doc/.docx on some Windows configs), and
// the backend requires the declared type, the filename extension, and the
// magic bytes to agree. Deriving the type from the (validated) extension
// makes the first two agree by construction; the backend still checks bytes.
const EXTENSION_TO_CONTENT_TYPE: Record<string, string> = {
  '.pdf': 'application/pdf',
  '.doc': 'application/msword',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
};

// Progressive disclosure: the form is grouped into collapsible sections so a
// first-time viewer sees all three headings (and knows a Principal's letter is
// coming) without scrolling a 14-field wall. Opening sections is always
// user-triggered; fields in collapsed sections stay MOUNTED (hidden with CSS,
// not unmounted) so react-hook-form registration and validation are unaffected.
const FORM_SECTIONS = [
  'Applicant and school details',
  'Assistance at your school',
  "Principal's letter",
];

// Which section each field lives in, so a failed submit can pop open every
// section that contains a validation error (nothing errors invisibly).
const FIELD_SECTION_INDEX: Record<string, number> = {
  school: 0,
  subscriptionStatus: 0,
  applicantName: 0,
  role: 0,
  email: 0,
  assistanceTerm: 1,
  stileXPurchaseIntent: 1,
  studentCountY7to10: 1,
  assistancePerStudent: 1,
  fundingReason: 1,
  stemImprovements: 1,
  principalName: 2,
  principalEmail: 2,
  principalLetter: 2,
};

function allowedLetterExtension(file: File): string | null {
  const lowerName = file.name.toLowerCase();
  return Object.keys(EXTENSION_TO_CONTENT_TYPE).find((ext) => lowerName.endsWith(ext)) ?? null;
}

// Case-insensitive match against the schools list, returning the list's
// canonical spelling. The payload sends the canonical name so the record
// carries the proper Account.Name capitalisation (the backend's own match is
// also case-insensitive); the backend stays the authority at submit time.
function canonicalSchool(value: string): string | undefined {
  const typed = value.trim().toLowerCase();
  return auSchools.find((school) => school.toLowerCase() === typed);
}

// Standard escape hatch: every stuck state ends at the same address with a
// "GAP application <what> — <school>" subject so the email thread is already
// tied to the school without the user re-typing anything. The school suffix is
// omitted when the school name isn't known yet.
const SUPPORT_EMAIL = 'subscriptions@stileeducation.com';

function supportMailto(what: string, school: string | null | undefined, body?: string): string {
  const trimmedSchool = school?.trim();
  const subject = `GAP application ${what}${trimmedSchool ? ` — ${trimmedSchool}` : ''}`;
  const params = [`subject=${encodeURIComponent(subject)}`];
  if (body) {
    params.push(`body=${encodeURIComponent(body)}`);
  }
  return `mailto:${SUPPORT_EMAIL}?${params.join('&')}`;
}

// Scroll a state-swap notice into view and move keyboard focus to it (the
// page height can collapse under the user when the form is replaced, leaving
// them stranded at the footer). Element needs tabIndex={-1}.
function scrollAndFocusNotice(element: HTMLElement) {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  element.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
  element.focus({ preventScroll: true });
}

type GapApplicationFormProps = {
  onSubmissionSuccess: () => void;
};

// The submission is two-phase so the application itself can never be lost to
// a failed 13 MB letter upload:
//   1. fields  — small POST; once this succeeds the application is captured.
//   2. letter  — big POST, retried up to LETTER_MAX_ATTEMPTS; on exhaustion
//                the user is told the application IS in and to email the
//                letter instead ('letterFallback').
type SubmitPhase =
  | { step: 'idle' }
  | { step: 'fields' }
  | { step: 'letter'; attempt: number; progress: number | null }
  | { step: 'fieldsError' }
  // 'transient': network/server trouble — same file may succeed, offer retry.
  // 'rejected': the server refused the file itself (400) — retrying the same
  // bytes cannot succeed, so email is the only path offered.
  // 'unreadable': the browser couldn't read the file (moved, deleted, or on
  // removed media) — retrying the same stale File handle cannot succeed, so
  // email is the only path offered.
  | { step: 'letterFallback'; reason: 'transient' | 'rejected' | 'unreadable' };

// GAP applications for a school year are accepted until 31 January of the
// following year, so January still belongs to the PREVIOUS application window
// — the year-dependent copy below must not tick over until 1 February.
// (Evaluated at build time and again on hydration; a stale prerendered year
// self-corrects on load.)
const now = new Date();
const currentYear = now.getFullYear() - (now.getMonth() === 0 ? 1 : 0);
const nextYear = currentYear + 1;

const subscriptionStatusOptions = [
  { value: '', text: 'Select an option' },
  {
    value: 'RENEWING',
    text: `Yes, we're looking to renew our subscription`,
  },
  {
    value: 'FIRST_YEAR',
    text: `No, ${nextYear} will be the first year we subscribe`,
  },
];

const assistanceTermOptions = [
  { value: '', text: 'Select an option' },
  {
    value: 'ONE_YEAR',
    text: `One year (${nextYear})`,
  },
  {
    value: 'THREE_YEARS',
    text: `Three years (${nextYear}–${nextYear + 2})`,
  },
];

const stileXPurchaseOptions = [
  { value: '', text: 'Select an option' },
  {
    value: 'YES',
    text: `Yes, we'd like to purchase Stile X booklets separately`,
  },
  {
    value: 'NO',
    text: `No, we'd like to purchase Stile only`,
  },
];

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Unexpected file read result'));
        return;
      }
      // result is a data URL: "data:<mime>;base64,<data>" — strip the prefix.
      const base64 = result.slice(result.indexOf(',') + 1);
      resolve(base64);
    };
    reader.readAsDataURL(file);
  });
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Phase 1 rejects an unrecognised school name with a field-level validation
// error (business rule: every eligible school already exists in Salesforce, so
// an unmatched name is almost always a typo). Captured response shape:
//   { "code": "VALIDATION_FAILED", "message": "Validation failed",
//     "errors": { "school": { "message": "school-not-found", "isExpected": false } } }
function isSchoolNotFoundRejection(error: unknown): boolean {
  if (!axios.isAxiosError(error) || error.response?.status !== 400) {
    return false;
  }
  const data = error.response.data as
    | { errors?: Record<string, { message?: string } | undefined> }
    | undefined;
  return data?.errors?.school?.message === 'school-not-found';
}

// Human copy for the backend's per-field validation codes. 'school-not-found'
// is handled separately (bespoke copy in onSubmit); an unknown code returns
// null so the field falls back to the generic submission-failed notice.
function serverErrorCopy(field: string, code: string | undefined): string | null {
  switch (code) {
    case 'required':
    // 'is-not-present' is the proto parse layer's missing-field code (the app
    // layer still returns 'required' for blank-but-present strings).
    case 'is-not-present':
      return 'This field is required';
    case 'too-long':
      return 'This answer is too long — please shorten it';
    // The parse layer rejects a non-integer/negative studentCountY7to10 with
    // 'is-invalid-value' before the app layer's format checks run.
    case 'is-invalid-value':
      if (field === 'studentCountY7to10') {
        return 'Please enter a whole number of students';
      }
      return null;
    case 'invalid-format':
      if (field === 'email' || field === 'principalEmail') {
        return 'Please enter a valid email address';
      }
      if (field === 'studentCountY7to10') {
        return 'Please enter a whole number of students';
      }
      if (field === 'assistancePerStudent') {
        return 'Please enter a valid dollar amount';
      }
      return 'Please check this answer and try again';
    default:
      return null;
  }
}

// Extract renderable inline field errors from a phase-1 400 validation
// rejection (same response shape as isSchoolNotFoundRejection documents).
// Returns null when the response carries nothing we can map to a known field —
// callers then fall back to the generic 'fieldsError' notice. With payload
// trimming and client-side validation these should be near-unreachable; this
// is belt-and-braces for the anonymous-endpoint contract.
function fieldsValidationRejectionErrors(
  error: unknown
): Partial<Record<keyof StileGapApplicationFields, { type: 'server'; message: string }>> | null {
  if (!axios.isAxiosError(error) || error.response?.status !== 400) {
    return null;
  }
  const data = error.response.data as
    | { errors?: Record<string, { message?: string } | undefined> }
    | undefined;
  const responseErrors = data?.errors;
  if (!responseErrors || typeof responseErrors !== 'object') {
    return null;
  }
  const mapped: Partial<
    Record<keyof StileGapApplicationFields, { type: 'server'; message: string }>
  > = {};
  for (const [field, detail] of Object.entries(responseErrors)) {
    if (!(field in FIELD_SECTION_INDEX)) {
      continue;
    }
    const message = serverErrorCopy(field, detail?.message);
    if (message) {
      mapped[field as keyof StileGapApplicationFields] = { type: 'server', message };
    }
  }
  return Object.keys(mapped).length > 0 ? mapped : null;
}

// The backend validates the raw payload values with NO trimming (" 50 " fails
// its dollar-amount regex), while client-side validation trims before
// checking — so trim every field once here, ensuring the backend sees exactly
// the value the client validated.
function trimFields(fields: StileGapApplicationFields): StileGapApplicationFields {
  return Object.fromEntries(
    Object.entries(fields).map(([key, value]) => [key, value.trim()])
  ) as StileGapApplicationFields;
}

// Phase 1: capture the application fields. Contract: 204 = captured (upsert on
// the idempotency key, so re-posting the same payload is safe).
async function submitFields(fields: StileGapApplicationFields | null, idempotencyKey: string) {
  if (!fields) {
    throw new Error('No fields payload to submit');
  }
  const auGapApplication: StileAuGapApplicationPayload = {
    ...fields,
    // Wire format is a JSON integer; client validation guarantees the trimmed
    // string is a positive whole number.
    studentCountY7to10: Number(fields.studentCountY7to10),
    idempotencyKey,
  };
  await axios.post(
    GAP_APPLICATION_FIELDS_API,
    { auGapApplication },
    { headers: { 'Content-Type': 'application/json' }, timeout: 30_000 }
  );
}

export function GapApplicationFormAu({ onSubmissionSuccess }: GapApplicationFormProps) {
  const {
    register,
    handleSubmit,
    reset: resetForm,
    watch,
    getValues,
    setValue,
    setFocus,
    setError,
    formState: { errors },
  } = useForm<StileGapApplicationFormData>();

  // Generated once per form instance (lazily, on the client) and kept stable
  // across retries and re-submissions, so the backend can dedupe both phases.
  // Regenerated only after a fully successful submission resets the form.
  const idempotencyKeyRef = React.useRef<string | null>(null);

  const [phase, setPhase] = React.useState<SubmitPhase>({ step: 'idle' });

  // Soft pre-submit warning: typed school doesn't exactly match the static
  // suggestion list. Never blocks — see the blur handler on the school field.
  const [schoolHint, setSchoolHint] = React.useState(false);
  const schoolHintTimerRef = React.useRef<number | undefined>(undefined);
  React.useEffect(() => () => window.clearTimeout(schoolHintTimerRef.current), []);

  // Kept so the letterFallback state can offer "try uploading again" without
  // re-submitting the fields, and so a 404 on the letter endpoint can re-run
  // phase 1 with the same payload.
  const pendingLetterRef = React.useRef<File | null>(null);
  const fieldsPayloadRef = React.useRef<StileGapApplicationFields | null>(null);

  // Synchronous guard against concurrent upload loops (the fallback screen's
  // retry button is clickable again before React state catches up).
  const uploadInFlightRef = React.useRef(false);
  // gap_application_letter_fallback_au must fire at most once per application:
  // the retry button re-runs uploadLetterWithRetries, and without this guard
  // every exhausted retry cycle would tag again (skewing the GTM subtraction
  // against gap_application_submit_au).
  const letterFallbackTaggedRef = React.useRef(false);
  const tagLetterFallbackOnce = () => {
    if (letterFallbackTaggedRef.current) {
      return;
    }
    letterFallbackTaggedRef.current = true;
    tagEvent('gap_application_letter_fallback_au', { page_path: window.location.pathname });
  };

  const isBusy = phase.step === 'fields' || phase.step === 'letter';

  // State-swap notices: scroll into view and take focus when they appear, so
  // a failure (or the form being replaced by the fallback) is never rendered
  // off-screen where the user can't see it.
  const fieldsErrorRef = React.useRef<HTMLDivElement>(null);
  const letterFallbackRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const notice =
      phase.step === 'fieldsError'
        ? fieldsErrorRef.current
        : phase.step === 'letterFallback'
        ? letterFallbackRef.current
        : null;
    if (notice) {
      scrollAndFocusNotice(notice);
    }
  }, [phase.step]);

  // Warn before leaving while work is in flight, or while the transient
  // fallback is showing — the pending letter File exists only in memory, so
  // navigating away loses the retry path forever.
  const guardUnload = isBusy || (phase.step === 'letterFallback' && phase.reason === 'transient');

  React.useEffect(() => {
    if (!guardUnload) {
      return;
    }
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [guardUnload]);

  // Independently collapsible sections — any number may be open at once.
  // First section open on load.
  const [openSections, setOpenSections] = React.useState<boolean[]>([true, false, false]);

  const toggleSection = (index: number) =>
    setOpenSections((open) => open.map((isOpen, i) => (i === index ? !isOpen : isOpen)));

  // Focus + scroll a field after a state change that may have just un-hidden
  // its section (double rAF waits out the display:none→visible re-render).
  const focusAndScrollField = (name: keyof StileGapApplicationFormData) =>
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        setFocus(name);
        const input = document.querySelector<HTMLElement>(`[name="${name}"]`);
        if (input) {
          if (document.activeElement !== input) {
            input.focus({ preventScroll: true });
          }
          const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          input.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
        }
      })
    );

  // Submit with validation errors: force open EVERY section containing one,
  // so nothing errors invisibly inside a collapsed section — then focus and
  // scroll to the first errored field. react-hook-form's own shouldFocusError
  // runs before the sections re-render, so a field inside a collapsed section
  // is display:none at that moment and its .focus() is a no-op; the double
  // requestAnimationFrame waits out the hidden→visible re-render.
  const openSectionsWithErrors = (fieldErrors: FieldErrors<StileGapApplicationFormData>) => {
    setOpenSections((open) =>
      open.map(
        (isOpen, i) =>
          isOpen || Object.keys(fieldErrors).some((name) => FIELD_SECTION_INDEX[name] === i)
      )
    );

    // FIELD_SECTION_INDEX is declared in DOM order, so the first errored key
    // in that order is the first errored field on screen.
    const firstErroredField = Object.keys(FIELD_SECTION_INDEX).find((name) => name in fieldErrors);
    if (firstErroredField) {
      focusAndScrollField(firstErroredField as keyof StileGapApplicationFormData);
    }
  };

  // "Answered" = the field has some input; live-updates via watch(). The file
  // input's value is a FileList rather than a string. FileList is browser-only,
  // so guard the instanceof for Gatsby's SSR pass (which renders the empty
  // form, so counts are 0 there — fine).
  const watchedValues = watch();
  const sectionCounts = FORM_SECTIONS.map((_, sectionIndex) => {
    const names = Object.keys(FIELD_SECTION_INDEX).filter(
      (name) => FIELD_SECTION_INDEX[name] === sectionIndex
    );
    const answered = names.filter((name) => {
      const value = watchedValues[name as keyof StileGapApplicationFormData];
      if (typeof FileList !== 'undefined' && value instanceof FileList) {
        return value.length > 0;
      }
      return typeof value === 'string' && value.trim() !== '';
    }).length;
    return { answered, total: names.length };
  });

  const sectionHeader = (index: number) => {
    const hasErrors = Object.keys(errors).some((name) => FIELD_SECTION_INDEX[name] === index);
    return (
      <button
        type="button"
        className="form-section-toggle"
        aria-expanded={openSections[index]}
        aria-controls={`gap-form-section-${index}`}
        onClick={() => toggleSection(index)}
      >
        <span>{FORM_SECTIONS[index]}</span>
        <span className={`form-section-count${hasErrors ? ' form-section-count-error' : ''}`}>
          {sectionCounts[index].answered} of {sectionCounts[index].total} answered
          {/* The red counter alone is colour-only signalling (WCAG 1.4.1). */}
          {hasErrors && <VisuallyHidden>, contains errors</VisuallyHidden>}
        </span>
        <span
          className={`form-section-chevron${
            openSections[index] ? ' form-section-chevron-open' : ''
          }`}
          aria-hidden="true"
        />
      </button>
    );
  };

  const uploadLetterWithRetries = async (file: File, idempotencyKey: string) => {
    if (uploadInFlightRef.current) {
      return;
    }
    uploadInFlightRef.current = true;
    try {
      let principalLetterBase64: string;
      try {
        principalLetterBase64 = await readFileAsBase64(file);
      } catch {
        // The file became unreadable after selection (moved, deleted, or on
        // removed media). The application fields are already captured, so the
        // email fallback is the honest state — never a stuck spinner.
        tagLetterFallbackOnce();
        setPhase({ step: 'letterFallback', reason: 'unreadable' });
        return;
      }

      const gapApplicationLetter: StileGapApplicationLetterPayload = {
        idempotencyKey,
        principalLetterFilename: file.name,
        // Validation guarantees an allowed extension; file.type is only a
        // last-resort fallback and should be unreachable.
        principalLetterContentType:
          EXTENSION_TO_CONTENT_TYPE[allowedLetterExtension(file) ?? ''] ?? file.type,
        principalLetter: { __type: 'stile.bytes.Base64', value: principalLetterBase64 },
      };

      let fallbackReason: 'transient' | 'rejected' = 'transient';

      for (let attempt = 1; attempt <= LETTER_MAX_ATTEMPTS; attempt++) {
        setPhase({ step: 'letter', attempt, progress: 0 });
        try {
          await axios.post(
            GAP_APPLICATION_LETTER_API,
            { gapApplicationLetter },
            {
              headers: { 'Content-Type': 'application/json' },
              // Backend budgets 180 s for the attach (slow school uplinks need
              // minutes for ~13.3 MB of base64); abort just after it would.
              timeout: 210_000,
              onUploadProgress: (event: AxiosProgressEvent) => {
                if (event.total) {
                  const progress = Math.min(100, Math.round((event.loaded / event.total) * 100));
                  setPhase({ step: 'letter', attempt, progress });
                }
              },
            }
          );

          // Full success: application + letter both in.
          idempotencyKeyRef.current = uuidv4();
          pendingLetterRef.current = null;
          resetForm();
          setPhase({ step: 'idle' });
          onSubmissionSuccess();
          return;
        } catch (error) {
          if (axios.isAxiosError(error)) {
            // 400: validation rejection (bad file) — the same bytes cannot pass
            // on retry, so go straight to the fallback.
            if (error.response?.status === 400) {
              fallbackReason = 'rejected';
              break;
            }
            // 404: phase 1's record isn't there (contract marks it expected) —
            // re-run phase 1 with the same idempotency key, then let the loop
            // retry the letter.
            if (error.response?.status === 404) {
              try {
                await submitFields(fieldsPayloadRef.current, idempotencyKey);
              } catch {
                // Phase 1 re-run failed too; fall through to normal backoff.
              }
            }
          }
          const backoff = LETTER_RETRY_BACKOFF_MS[attempt - 1];
          if (attempt < LETTER_MAX_ATTEMPTS && backoff) {
            await wait(jitteredBackoff(backoff));
          }
        }
      }

      // Letter exhausted its attempts (or was rejected outright) — the
      // application itself is already safely submitted, so tell the user to
      // send the letter by email.
      tagLetterFallbackOnce();
      setPhase({ step: 'letterFallback', reason: fallbackReason });
    } finally {
      uploadInFlightRef.current = false;
    }
  };

  const onSubmit = async (formData: StileGapApplicationFormData) => {
    // Re-entrancy guard: a second click can land before the disabling
    // re-render, double-firing phase 1 and the submit analytics event.
    if (isBusy || uploadInFlightRef.current) {
      return;
    }
    if (idempotencyKeyRef.current === null) {
      idempotencyKeyRef.current = uuidv4();
    }
    const idempotencyKey = idempotencyKeyRef.current;
    const file = formData.principalLetter[0];
    pendingLetterRef.current = file;

    fieldsPayloadRef.current = trimFields({
      // Canonical list spelling, not the user's typing, so what staff see in
      // Salesforce is properly capitalised. Validation guarantees a match
      // exists; the typed value is only a fallback for an unreachable state.
      school: canonicalSchool(formData.school) ?? formData.school,
      subscriptionStatus: formData.subscriptionStatus,
      assistanceTerm: formData.assistanceTerm,
      stileXPurchaseIntent: formData.stileXPurchaseIntent,
      applicantName: formData.applicantName,
      role: formData.role,
      email: formData.email,
      principalName: formData.principalName,
      principalEmail: formData.principalEmail,
      studentCountY7to10: formData.studentCountY7to10,
      assistancePerStudent: formData.assistancePerStudent,
      fundingReason: formData.fundingReason,
      stemImprovements: formData.stemImprovements,
    });

    // Phase 1: the application fields. Small request; once it lands the
    // application can no longer be lost.
    setPhase({ step: 'fields' });
    try {
      await submitFields(fieldsPayloadRef.current, idempotencyKey);
    } catch (error) {
      if (isSchoolNotFoundRejection(error)) {
        // Inline field error, not the generic notice: the fix is editing the
        // school field, and the standard errors path shows the red section
        // counter and inline message for free.
        setError('school', {
          type: 'server',
          message:
            "We couldn't find your school — check the spelling, or choose it from the suggestions as you type. If your school still isn't found, email subscriptions@stileeducation.com and we'll sort it out.",
        });
        const schoolSection = FIELD_SECTION_INDEX['school'];
        setOpenSections((open) => open.map((isOpen, i) => (i === schoolSection ? true : isOpen)));
        focusAndScrollField('school');
        setPhase({ step: 'idle' });
        return;
      }
      // Any other 400 with a recognisable per-field errors map: render inline
      // field errors, mirroring the school path — a retry of the same payload
      // would deterministically fail, so the generic "try again" notice would
      // only mislead. openSectionsWithErrors force-opens every section with an
      // errored field and focuses/scrolls the first.
      const serverFieldErrors = fieldsValidationRejectionErrors(error);
      if (serverFieldErrors) {
        for (const [field, fieldError] of Object.entries(serverFieldErrors)) {
          if (fieldError) {
            setError(field as keyof StileGapApplicationFormData, fieldError);
          }
        }
        openSectionsWithErrors(serverFieldErrors);
        setPhase({ step: 'idle' });
        return;
      }
      setPhase({ step: 'fieldsError' });
      return;
    }

    // The application is captured from here on — count the submission now,
    // whether or not the letter upload succeeds. letter_fallback is guarded
    // to fire at most once per application (tagLetterFallbackOnce), so the
    // two events subtract cleanly in GTM.
    tagEvent('gap_application_submit_au', { page_path: window.location.pathname });

    // Phase 2: the letter, retried independently of the fields.
    await uploadLetterWithRetries(file, idempotencyKey);
  };

  const retryLetterUpload = () => {
    const file = pendingLetterRef.current;
    const idempotencyKey = idempotencyKeyRef.current;
    if (file && idempotencyKey) {
      // Immediate feedback: the base64 re-read can take seconds before the
      // upload loop's first progress event.
      setPhase({ step: 'letter', attempt: 1, progress: null });
      void uploadLetterWithRetries(file, idempotencyKey);
    }
  };

  if (phase.step === 'letterFallback') {
    // School name is guaranteed here: fieldsPayloadRef is set before phase 1
    // fires, and letterFallback is only reachable after phase 1 succeeds.
    const school = fieldsPayloadRef.current?.school?.trim() ?? '';
    const letterFileName = pendingLetterRef.current?.name;
    const mailtoBody = `Hi Stile team,\r\n\r\nPlease find attached the Principal's letter for ${
      school ? `${school}'s` : "our school's"
    } Grants and Assistance application.`;
    const emailLink = (
      <a href={supportMailto("Principal's letter", school, mailtoBody)}>{SUPPORT_EMAIL}</a>
    );
    const letterFileNameEm = letterFileName ? <em>{letterFileName}</em> : null;

    // role="alert" lives on the text block, not this container: the container
    // holds an interactive Button (discouraged inside a live region) and takes
    // keyboard focus instead.
    return (
      <div
        className="form-submit-notice form-submit-notice-fallback"
        ref={letterFallbackRef}
        tabIndex={-1}
      >
        {phase.reason === 'rejected' ? (
          <div className="form-notice-text" role="alert">
            <p className="form-submit-notice-lead">
              <span className="form-submit-notice-tick" aria-hidden="true">
                ✓
              </span>{' '}
              <strong>Your application has been received.</strong>
            </p>
            <p>
              One thing left: we couldn&apos;t read {letterFileNameEm ?? "your Principal's letter"}{' '}
              as a PDF, DOC, DOCX, JPG, or PNG file. This can happen if the file was renamed or
              didn&apos;t finish downloading.
            </p>
            <p>
              Please check the file opens on your computer, then email it to {emailLink} —
              we&apos;ve pre-filled the subject line for you.
            </p>
          </div>
        ) : phase.reason === 'unreadable' ? (
          <div className="form-notice-text" role="alert">
            <p className="form-submit-notice-lead">
              <span className="form-submit-notice-tick" aria-hidden="true">
                ✓
              </span>{' '}
              <strong>Your application has been received.</strong>
            </p>
            <p>
              One thing left: we couldn&apos;t read {letterFileNameEm ?? "your Principal's letter"}{' '}
              from your device — this can happen if the file was moved, renamed, or deleted after
              you selected it.
            </p>
            <p>
              Please check the file still exists and opens on your computer, then email it to{' '}
              {emailLink} — we&apos;ve pre-filled the subject line for you.
            </p>
          </div>
        ) : (
          <>
            <div className="form-notice-text" role="alert">
              <p className="form-submit-notice-lead">
                <span className="form-submit-notice-tick" aria-hidden="true">
                  ✓
                </span>{' '}
                <strong>Your application has been received.</strong>
              </p>
              <p>
                One thing left: your Principal&apos;s letter
                {letterFileNameEm ? <> ({letterFileNameEm})</> : null} didn&apos;t finish uploading
                — this is usually just a network hiccup.
              </p>
            </div>
            <Button label="Try uploading the letter again" onClick={retryLetterUpload} />
            <p>
              Or email the letter to {emailLink} — we&apos;ve pre-filled the subject line for you.
            </p>
          </>
        )}
      </div>
    );
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit, openSectionsWithErrors)}>
      <div className="form-section">
        {sectionHeader(0)}
        <div id="gap-form-section-0" className="form-section-content" hidden={!openSections[0]}>
          <FormField
            onBlur={() => {
              // Early warning for the in-list rule the validate option below
              // enforces at submit: an application needs a school the backend
              // can match to a Salesforce Account, so a name outside the list
              // is a genuine blocker. The backend re-checks against the live
              // table at submit either way.
              //
              // DEFERRED past the click cascade: blur fires on mousedown, and
              // rendering the warning synchronously shifts the layout before
              // mouseup — swallowing the very click (often Submit) that caused
              // the blur. The timer runs after mouseup/click dispatch, so the
              // click always lands first.
              const typed = getValues('school')?.trim() ?? '';
              const canonical = canonicalSchool(typed);
              // Visible auto-correct: snap the field to the list's spelling on
              // blur so what the user sees is exactly what will be submitted.
              // Reach's ComboboxInput is controlled by its own state machine,
              // which only updates from real input events — RHF setValue writes
              // state Reach immediately renders over. Dispatching a native
              // input event routes the correction through the same pipeline as
              // typing (Reach machine + RHF onChange), keeping all three in
              // sync.
              if (canonical !== undefined && canonical !== typed) {
                const input = document.getElementById('school-input');
                if (input instanceof HTMLInputElement) {
                  const setNativeValue = Object.getOwnPropertyDescriptor(
                    window.HTMLInputElement.prototype,
                    'value'
                  )?.set;
                  setNativeValue?.call(input, canonical);
                  input.dispatchEvent(new Event('input', { bubbles: true }));
                  // The synthetic input reopens Reach's suggestion popover on
                  // a field that no longer has focus; a synthetic focusout
                  // closes it. (Re-enters this handler once, which no-ops now
                  // that the value is canonical.)
                  input.dispatchEvent(new FocusEvent('focusout', { bubbles: true }));
                } else {
                  setValue('school', canonical);
                }
              }
              const showHint = typed.length >= 4 && canonical === undefined;
              window.clearTimeout(schoolHintTimerRef.current);
              schoolHintTimerRef.current = window.setTimeout(() => setSchoolHint(showHint), 150);
            }}
            onFocus={() => {
              window.clearTimeout(schoolHintTimerRef.current);
              setSchoolHint(false);
            }}
          >
            <label htmlFor="school-input">School name*</label>
            <ComboBox
              ariaLabel="School name"
              id="school-input"
              name="school"
              placeholder="E.g. St Mary's Catholic College"
              options={auSchools}
              aria-invalid={errors.school ? 'true' : 'false'}
              register={register}
              validationOptions={{
                maxLength: { value: 200, message: '200 character limit' },
                required: 'This field is required',
                validate: (value: string) =>
                  canonicalSchool(value) !== undefined ||
                  "We couldn't find your school — check the spelling, or choose it from the suggestions as you type. If your school still isn't found, email subscriptions@stileeducation.com and we'll sort it out.",
              }}
            />
            {schoolHint && !errors.school && (
              // Error styling is honest here: the validate rule above blocks
              // submission on the same in-list check, this just surfaces it
              // at blur instead of at submit.
              <p className="form-field-error-2024">
                We couldn&apos;t find your school — check the spelling, or choose it from the
                suggestions as you type. If your school still isn&apos;t found, email
                subscriptions@stileeducation.com and we&apos;ll sort it out.
              </p>
            )}
            <FormFieldError name="school" errors={errors} />
          </FormField>

          <FormField>
            <Select
              label={`Did your school have a paid subscription to Stile in ${currentYear}?*`}
              options={subscriptionStatusOptions}
              aria-invalid={errors.subscriptionStatus ? 'true' : 'false'}
              {...register('subscriptionStatus', { required: 'This field is required' })}
            />
            <FormFieldError name="subscriptionStatus" errors={errors} />
          </FormField>

          <FormField>
            <Input
              {...register('applicantName', {
                maxLength: { value: 200, message: '200 character limit' },
                required: 'This field is required',
                validate: requiredNotBlank,
              })}
              label="Name of applicant*"
              placeholder="E.g. Sally Smith"
              aria-invalid={errors.applicantName ? 'true' : 'false'}
            />
            <FormFieldError name="applicantName" errors={errors} />
          </FormField>

          <FormField>
            <Select
              label="Position at your school*"
              options={staffOnlyRoleSelectOptions}
              aria-invalid={errors.role ? 'true' : 'false'}
              {...register('role', { required: 'This field is required' })}
            />
            <FormFieldError name="role" errors={errors} />
          </FormField>

          <FormField>
            <Input
              type="email"
              label="Applicant's email address*"
              placeholder="name@example.com"
              aria-invalid={errors.email ? 'true' : 'false'}
              {...register('email', {
                maxLength: { value: 200, message: '200 character limit' },
                required: 'This field is required',
                pattern: {
                  value: emailValidator,
                  message: 'Email address invalid',
                },
              })}
            />
            <p className="form-field-hint">
              Will be used to advise whether this application is successful.
            </p>
            <FormFieldError name="email" errors={errors} />
          </FormField>
        </div>
      </div>

      <div className="form-section">
        {sectionHeader(1)}
        <div id="gap-form-section-1" className="form-section-content" hidden={!openSections[1]}>
          <FormField>
            <Select
              label="For how many years are you applying for assistance?*"
              options={assistanceTermOptions}
              aria-invalid={errors.assistanceTerm ? 'true' : 'false'}
              {...register('assistanceTerm', { required: 'This field is required' })}
            />
            <FormFieldError name="assistanceTerm" errors={errors} />
          </FormField>

          <FormField>
            <Select
              label="Is your school looking to purchase Stile X booklets?*"
              options={stileXPurchaseOptions}
              aria-invalid={errors.stileXPurchaseIntent ? 'true' : 'false'}
              {...register('stileXPurchaseIntent', { required: 'This field is required' })}
            />
            <p className="form-field-hint">
              Stile X is our notebook and revision guide for students. Booklets must be purchased
              separately and cannot be subsidised through the Grants and Assistance Program.
            </p>
            <FormFieldError name="stileXPurchaseIntent" errors={errors} />
          </FormField>

          <FormField>
            <Input
              type="number"
              inputMode="numeric"
              min={1}
              label="What is the estimated total number of students in Years 7–10 at your school?*"
              placeholder="Type your answer here..."
              aria-invalid={errors.studentCountY7to10 ? 'true' : 'false'}
              {...register('studentCountY7to10', {
                required: 'This field is required',
                validate: (value) =>
                  /^\d+$/.test(String(value).trim()) && Number(value) > 0
                    ? true
                    : 'Enter a whole number of students',
              })}
            />
            <p className="form-field-hint">
              Note that GAP is only available for schools adopting Stile for all students across
              Years 7–10.
            </p>
            <FormFieldError name="studentCountY7to10" errors={errors} />
          </FormField>

          <FormField>
            <Input
              label="What is the price per student you are seeking?*"
              placeholder="E.g. 25"
              inputMode="decimal"
              aria-invalid={errors.assistancePerStudent ? 'true' : 'false'}
              {...register('assistancePerStudent', {
                required: 'This field is required',
                validate: (value) =>
                  /^\d+(\.\d{1,2})?$/.test(value.trim()) ? true : 'Enter a valid dollar amount',
              })}
            />
            <p className="form-field-hint">
              You can find your current subscription cost per student on the{' '}
              <a
                href="https://help.stileapp.com/en/articles/65409-manage-your-school-s-stile-subscription"
                target="_blank"
                rel="noreferrer"
              >
                Subscription Management page
              </a>{' '}
              in Stile.
            </p>
            <FormFieldError name="assistancePerStudent" errors={errors} />
          </FormField>

          <FormField>
            <TextArea
              label="Please tell us why your school is seeking funding and support via Stile's Grants and Assistance Program. Please provide as much detail as possible.*"
              placeholder="Type your answer here..."
              aria-invalid={errors.fundingReason ? 'true' : 'false'}
              {...register('fundingReason', {
                required: 'This field is required',
                validate: requiredNotBlank,
                maxLength: { value: 5000, message: '5000 character limit' },
              })}
            />
            <div className="form-field-hint">
              <p>This may include:</p>
              <ul>
                <li>the socio-economic circumstances of your school.</li>
                <li>
                  advising whether your school typically collects payments from families e.g.
                  booklist
                </li>
                <li>
                  outlining the availability of funds in relevant budgets e.g. science budget, STEM
                  budget, ICT budget etc.
                </li>
              </ul>
            </div>
            <FormFieldError name="fundingReason" errors={errors} />
          </FormField>

          <FormField>
            <TextArea
              label="Other than adopting Stile, how else are you looking to improve STEM education at your school?*"
              placeholder="Type your answer here..."
              aria-invalid={errors.stemImprovements ? 'true' : 'false'}
              {...register('stemImprovements', {
                required: 'This field is required',
                validate: requiredNotBlank,
                maxLength: { value: 5000, message: '5000 character limit' },
              })}
            />
            <FormFieldError name="stemImprovements" errors={errors} />
          </FormField>
        </div>
      </div>

      <div className="form-section">
        {sectionHeader(2)}
        <div id="gap-form-section-2" className="form-section-content" hidden={!openSections[2]}>
          <FormField>
            <Input
              {...register('principalName', {
                maxLength: { value: 200, message: '200 character limit' },
                required: 'This field is required',
                validate: requiredNotBlank,
              })}
              label="Principal name*"
              placeholder="Type your answer here..."
              aria-invalid={errors.principalName ? 'true' : 'false'}
            />
            <p className="form-field-hint">
              Your school Principal will also be advised as to whether this application is
              successful.
            </p>
            <FormFieldError name="principalName" errors={errors} />
          </FormField>

          <FormField>
            <Input
              type="email"
              label="Principal's email address*"
              placeholder="name@example.com"
              aria-invalid={errors.principalEmail ? 'true' : 'false'}
              {...register('principalEmail', {
                maxLength: { value: 200, message: '200 character limit' },
                required: 'This field is required',
                pattern: {
                  value: emailValidator,
                  message: 'Email address invalid',
                },
              })}
            />
            <FormFieldError name="principalEmail" errors={errors} />
          </FormField>

          <FormField>
            <label htmlFor="principalLetter">
              Please upload a short letter from the school&apos;s Principal, supporting this
              application.*
            </label>
            <input
              id="principalLetter"
              type="file"
              accept="application/pdf,.pdf,application/msword,.doc,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx,image/jpeg,.jpg,.jpeg,image/png,.png"
              aria-invalid={errors.principalLetter ? 'true' : 'false'}
              {...register('principalLetter', {
                required: 'This field is required',
                validate: (files: FileList) => {
                  const file = files?.[0];
                  if (!file) {
                    return 'This field is required';
                  }
                  if (allowedLetterExtension(file) === null) {
                    return 'Please upload a PDF, DOC, DOCX, JPG, or PNG file';
                  }
                  if (file.size > MAX_LETTER_BYTES) {
                    return 'File must be 10MB or smaller';
                  }
                  return true;
                },
              })}
            />
            <p className="form-field-hint">PDF, DOC, DOCX, JPG, or PNG. Maximum file size: 10MB.</p>
            <FormFieldError name="principalLetter" errors={errors} />
          </FormField>
        </div>
      </div>

      {isBusy && (
        <>
          <div className="form-upload-status" role="status" aria-live="polite">
            <span className="form-upload-status-spinner" aria-hidden="true" />
            {phase.step === 'fields' ? (
              <span>Submitting your application…</span>
            ) : phase.progress === null ? (
              <span>Preparing your letter…</span>
            ) : phase.progress < 100 ? (
              <>
                <span>
                  {phase.attempt > 1
                    ? "Still working — that upload didn't get through, trying again…"
                    : 'Uploading your letter…'}{' '}
                  {/* aria-hidden so every progress tick doesn't re-announce the
                      live region; the visual bar carries the percentage. */}
                  <span aria-hidden="true">{phase.progress}%</span>
                </span>
                <span className="form-upload-progress-track" aria-hidden="true">
                  <span
                    className="form-upload-progress-fill"
                    style={{ width: `${phase.progress}%` }}
                  />
                </span>
              </>
            ) : (
              <span>Upload complete — saving your letter…</span>
            )}
          </div>
          {/* The beforeunload guard can't catch Gatsby's client-side (pushState)
              navigations, and the letter File exists only in memory — surface
              the email fallback before the user can navigate away and lose it.
              Outside the live region so it isn't announced on progress updates. */}
          {phase.step === 'letter' && (
            <p className="form-field-hint">
              Please stay on this page until the upload finishes. If you do leave early, email your
              letter to{' '}
              <a href={supportMailto("Principal's letter", fieldsPayloadRef.current?.school)}>
                {SUPPORT_EMAIL}
              </a>{' '}
              instead.
            </p>
          )}
        </>
      )}

      {phase.step === 'fieldsError' && (
        <div className="form-error-notice" ref={fieldsErrorRef} tabIndex={-1}>
          <div className="form-notice-text" role="alert">
            <p>
              <strong>We couldn&apos;t submit your application just now.</strong>
            </p>
            <p>
              Don&apos;t worry — everything you&apos;ve typed is still saved in the form above.
              Please press <strong>Submit</strong> to try again.
            </p>
            <p>
              If it keeps failing, email us at{' '}
              <a href={supportMailto('form issue', fieldsPayloadRef.current?.school)}>
                {SUPPORT_EMAIL}
              </a>{' '}
              and we&apos;ll take your application by email.
            </p>
          </div>
        </div>
      )}

      <Button label={isBusy ? 'Submitting…' : 'Submit'} type="submit" disabled={isBusy} />
    </Form>
  );
}
